<template>
  <ChartCard :data-widget-id="widgetId" :id-for-aria="widgetId">
    <ChartHeader
      :label="widgetLabel"
      :widget-download-id="widgetId"
      tooltip-key="source-categories-for-apps"
    />
    <ChartContent
      height="284px"
      :empty-data="emptyData || noWidgetData"
      :error="error"
      :loading="loading"
      type="horizontalBarChart"
      class="px-3 pt-2 pb-0"
    >
      <BarChart :id="widgetId" :data="chartData" :options="chartOptions" :plugins="plugins" title="Source categories of applications" />
    </ChartContent>
  </ChartCard>
</template>

<script setup>
import ChartHeader from '@/components/insights/Chart/ChartHeader.vue';
import ChartCard from '@/components/insights/Chart/ChartCard.vue';
import ChartContent from '@/components/insights/Chart/ChartContent.vue';
import BarChart from '@/components/insights/Chart/BarChart.vue';
import { computed } from 'vue';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { calculatePercent } from '@/components/insights/helpers';
import {
  dataLabelsConfig,
  flippedAxisTooltipPluginConfig,
  xScaleWithPercentFormatConfig,
  basicYScaleConfig,
} from '@/api/helpers/insights/commonChartConfigs';
import { startCase } from 'lodash-es';
import { insightsColorMap } from '@/api/helpers/insights/colorMap';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  emptyData: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const widgetLabel = 'What source categories do our applications come from?';

const plugins = [ChartDataLabels];

const widgetId = 'overall-top-sources-by-application';

const noWidgetData = computed(() => props.data.length === 0
  || props.data.every((item) => item.review === 0));

const chartData = computed(() => {
  const sorted = [...props.data].sort((a, b) => b.review - a.review);
  const totalReview = sorted.reduce((acc, item) => acc + item.review, 0);
  const labels = sorted.map((item) => startCase(item.category));
  const colors = labels.map((label) => insightsColorMap()[label]);

  return {
    labels,
    datasets: [
      {
        label: '',
        backgroundColor: colors,
        borderColor: colors,
        fill: false,
        maxBarThickness: 24,
        barThickness: 'flex',
        data: sorted.map((item) => calculatePercent(item.review, totalReview)),
      },
    ],
  };
});

const chartOptions = computed(() => ({
  maintainAspectRatio: false,
  responsive: true,
  indexAxis: 'y',
  plugins: {
    legend: { display: false },
    tooltip: flippedAxisTooltipPluginConfig(chartData, widgetId),
    datalabels: dataLabelsConfig,
  },
  scales: {
    x: xScaleWithPercentFormatConfig,
    y: basicYScaleConfig,
  },
}));
</script>
